import { useEffect, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import type { Components } from 'react-markdown'

interface MarkdownMessageProps {
  content: string
  className?: string
}

// Mermaid is heavy, so it is loaded lazily (its own chunk) the first time a
// ```mermaid block actually appears — the base chat bundle stays lean.
let mermaidPromise: Promise<any> | null = null
function loadMermaid(): Promise<any> {
  if (!mermaidPromise) {
    mermaidPromise = import('mermaid').then((mod) => {
      const mermaid = mod.default
      mermaid.initialize({ startOnLoad: false, theme: 'dark', securityLevel: 'strict' , fontFamily: 'var(--font-ui, "trebuchet ms", verdana, arial, sans-serif)' })
      return mermaid
    })
  }
  return mermaidPromise
}

let mermaidSeq = 0

/** Render a mermaid graph; fall back to the raw source on any parse error so a
 * malformed diagram never blanks the message. */
function MermaidDiagram({ code }: { code: string }) {
  const ref = useRef<HTMLDivElement>(null)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    let active = true
    setError(null)
    loadMermaid()
      .then(async (mermaid) => {
        try {
          const id = `mmd-${Date.now()}-${mermaidSeq++}`
          const { svg } = await mermaid.render(id, code)
          if (active && ref.current) ref.current.innerHTML = svg
        } catch (e: any) {
          if (active) setError(String(e?.message || e))
        }
      })
      .catch((e) => { if (active) setError(String(e)) })
    return () => { active = false }
  }, [code])
  if (error) {
    return (
      <pre className="block bg-surface-alt rounded-md p-3 my-2 text-xs font-mono overflow-x-auto whitespace-pre border border-soft">
        {code}
      </pre>
    )
  }
  return <div ref={ref} className="my-2 overflow-x-auto flex justify-center" />
}

/** Flatten a react-markdown code child (usually a string) to plain text. */
function nodeToText(node: unknown): string {
  if (typeof node === 'string') return node
  if (Array.isArray(node)) return node.map(nodeToText).join('')
  if (node && typeof node === 'object' && 'props' in (node as any)) {
    return nodeToText((node as any).props?.children)
  }
  return ''
}

const components: Components = {
  h1: ({ children }) => (
    <h1 className="text-lg font-bold mt-4 mb-2 text-primary">{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 className="text-base font-bold mt-3 mb-1.5 text-primary">{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className="text-sm font-bold mt-2.5 mb-1 text-primary">{children}</h3>
  ),
  p: ({ children }) => (
    <p className="mb-2 last:mb-0 leading-relaxed">{children}</p>
  ),
  ul: ({ children }) => (
    <ul className="list-disc pl-5 mb-2 space-y-0.5">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="list-decimal pl-5 mb-2 space-y-0.5">{children}</ol>
  ),
  li: ({ children }) => (
    <li className="leading-relaxed">{children}</li>
  ),
  strong: ({ children }) => (
    <strong className="font-semibold">{children}</strong>
  ),
  em: ({ children }) => (
    <em className="italic">{children}</em>
  ),
  blockquote: ({ children }) => (
    <blockquote className="border-l-3 border-brand pl-3 my-2 text-secondary italic">
      {children}
    </blockquote>
  ),
  code: ({ className, children }) => {
    const isBlock = className?.includes('language-')
    if (isBlock) {
      return (
        <code className="block bg-surface-alt rounded-md p-3 my-2 text-xs font-mono overflow-x-auto whitespace-pre border border-soft">
          {children}
        </code>
      )
    }
    return (
      <code className="bg-surface-alt rounded px-1.5 py-0.5 text-xs font-mono border border-soft">
        {children}
      </code>
    )
  },
  pre: ({ children }) => {
    // Intercept ```mermaid fences and render a diagram instead of a code block.
    // Done at the `pre` level (not `code`) so we never nest a <div>/<svg> inside
    // a <pre>, which is invalid markup.
    const child: any = Array.isArray(children) ? children[0] : children
    const cls: string = child?.props?.className || ''
    if (typeof cls === 'string' && cls.includes('language-mermaid')) {
      return <MermaidDiagram code={nodeToText(child?.props?.children).replace(/\n$/, '')} />
    }
    return <pre className="my-2">{children}</pre>
  },
  img: ({ src, alt, title }) => (
    <img
      src={typeof src === 'string' ? src : undefined}
      alt={alt || ''}
      title={title}
      loading="lazy"
      className="max-w-full h-auto rounded-md my-2 border border-soft"
    />
  ),
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-brand underline hover:text-brand-hover"
    >
      {children}
    </a>
  ),
  hr: () => (
    <hr className="my-3 border-soft" />
  ),
  table: ({ children }) => (
    <div className="overflow-x-auto my-2">
      <table className="min-w-full text-xs border border-soft rounded">
        {children}
      </table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="bg-surface-alt">{children}</thead>
  ),
  th: ({ children }) => (
    <th className="px-3 py-1.5 text-left font-semibold border-b border-soft">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="px-3 py-1.5 border-b border-soft">
      {children}
    </td>
  ),
}

export function MarkdownMessage({ content, className = '' }: MarkdownMessageProps) {
  return (
    <div className={`text-sm leading-relaxed ${className}`}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {content}
      </ReactMarkdown>
    </div>
  )
}

export default MarkdownMessage
